/**
 * Module-level controller for stopping active dictation sessions.
 *
 * The key insight is that we must stop dictation at the adapter level,
 * not through the runtime. When threads switch, the runtime reference
 * becomes stale, but the adapters maintain their own session tracking.
 *
 * Adapters register their stop functions via registerAdapterStop(), and
 * stopActiveDictation() calls all registered stop functions.
 */

type StopFn = () => void;
const adapterStopFns: Set<StopFn> = new Set();

/**
 * Register an adapter's stop function.
 * Returns an unregister function.
 */
export function registerAdapterStop(stopFn: StopFn): () => void {
  adapterStopFns.add(stopFn);
  return () => adapterStopFns.delete(stopFn);
}

let activeDictation = false;

/** Mark that dictation has started (called when dictation begins) */
export function setActiveDictationStop(): void {
  activeDictation = true;
}

/** Clear the active dictation flag (called after dictation ends normally) */
export function clearActiveDictationStop(): void {
  activeDictation = false;
}

/**
 * Stop the active dictation session if one exists.
 * This calls all registered adapter stop functions to ensure
 * the actual dictation session is stopped regardless of which provider is active.
 */
export function stopActiveDictation(): void {
  if (activeDictation) {
    adapterStopFns.forEach(stop => stop());
    activeDictation = false;
  }
}

/**
 * Reset the registry for testing purposes.
 */
export function resetRegistryForTesting(): void {
  adapterStopFns.clear();
  activeDictation = false;
}
